import {ReactNode} from 'react'
import {Route, Routes, Navigate, Outlet} from 'react-router-dom'
import {PublicLayout} from '../pages/public/PublicLayout'
import {HomePage} from '../pages/public/HomePage'
import {RoomsPage} from '../pages/public/RoomsPage'
import {ListingDetailPage} from '../pages/public/ListingDetailPage'
import {AboutPage} from '../pages/public/AboutPage'
import {FaqPage} from '../pages/public/FaqPage'
import {ContactPage} from '../pages/public/ContactPage'
import {InfoPage} from '../pages/public/InfoPage'
import {Login} from '../modules/auth/components/Login'
import {Registration} from '../modules/auth/components/Registration'
import {ForgotPassword} from '../modules/auth/components/ForgotPassword'
import {ResetPassword} from '../modules/auth/components/ResetPassword'
import {useAuth} from '../modules/auth'
import {RenterHome} from '../pages/renter/RenterHome'
import {BookingDetailsPage} from '../pages/renter/BookingDetailsPage'
import {ProfilePage} from '../pages/renter/ProfilePage'
import {ApplyPage} from '../pages/renter/ApplyPage'
import {BookPage} from '../pages/renter/BookPage'
import {RenterPaymentsPage} from '../pages/renter/RenterPaymentsPage'
import {RequestsPage} from '../pages/renter/RequestsPage'
import {AnnouncementsPage as RenterAnnouncementsPage} from '../pages/renter/AnnouncementsPage'

/** Centered auth card on a soft backdrop, inside the public chrome. */
const AuthScreen = ({children}: {children: ReactNode}) => (
  <div style={{minHeight: '74vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(28px, 7vw, 56px) clamp(14px, 4vw, 20px)', background: 'linear-gradient(160deg, #f1faf4 0%, #f4f6fa 100%)'}}>
    {children}
  </div>
)

/** Constrained container for renter dashboard pages, nested inside PublicLayout. */
const RenterShell = () => (
  <div style={{maxWidth: 1140, margin: '0 auto', padding: '20px 20px 44px', width: '100%', boxSizing: 'border-box'}}>
    <Outlet />
  </div>
)

/**
 * Guest + renter site. Everyone shares the public layout (marketing pages). Guests
 * also get the auth flow; signed-in renters additionally get their dashboard
 * pages (home/apply/book/payments/profile) under the same chrome.
 */
const PublicRoutes = () => {
  const {currentUser} = useAuth()
  const isAuthed = !!currentUser

  return (
    <Routes>
      <Route element={<PublicLayout />}>
        {/* Marketing — visible to everyone */}
        <Route index element={<HomePage />} />
        <Route path='rooms' element={<RoomsPage />} />
        <Route path='rooms/:slug' element={<ListingDetailPage />} />
        <Route path='about' element={<AboutPage />} />
        <Route path='faq' element={<FaqPage />} />
        <Route path='contact' element={<ContactPage />} />
        <Route path='info/:slug' element={<InfoPage />} />

        {isAuthed ? (
          <>
            {/* Renter dashboard — same chrome, constrained container */}
            <Route element={<RenterShell />}>
              <Route path='home' element={<RenterHome />} />
              <Route path='booking' element={<BookingDetailsPage />} />
              <Route path='apply' element={<ApplyPage />} />
              <Route path='book' element={<BookPage />} />
              <Route path='payments' element={<RenterPaymentsPage />} />
              <Route path='requests' element={<RequestsPage />} />
              <Route path='announcements' element={<RenterAnnouncementsPage />} />
              <Route path='profile' element={<ProfilePage />} />
            </Route>
            {/* Already signed in — auth pages bounce to the dashboard */}
            <Route path='auth/*' element={<Navigate to='/home' replace />} />
          </>
        ) : (
          <>
            {/* Auth flow — inside the public layout */}
            <Route path='auth' element={<Navigate to='/auth/login' replace />} />
            <Route path='auth/login' element={<AuthScreen><Login /></AuthScreen>} />
            <Route path='auth/registration' element={<AuthScreen><Registration /></AuthScreen>} />
            <Route path='auth/forgot-password' element={<AuthScreen><ForgotPassword /></AuthScreen>} />
            <Route path='auth/reset-password' element={<AuthScreen><ResetPassword /></AuthScreen>} />
          </>
        )}

        <Route path='*' element={<Navigate to='/' />} />
      </Route>
    </Routes>
  )
}

export {PublicRoutes}
